/**
 * ClientOfferReviewShell
 *
 * Composes the redesigned client-offer-review-v2 component set (header,
 * status banner, summary stats, toolbar, unit list, map panel, lightbox,
 * confirmation modals, footer bar) against real API data. Rendered by
 * SharedClientOfferContent when the client-offer-review-v2 flag is on — see
 * that file for the flag branching. Composes the existing
 * useClientOfferSelections (decisions/periods) and
 * useClientOfferReviewViewState (search/filter/sort/photo/page/layout)
 * rather than reimplementing either.
 *
 * No unit detail modal — "Zobacz szczegóły" navigates to the client
 * media-unit detail route instead (client-offer-interactive-view / Unit
 * Detail Entry Point and Photo Lightbox; the retired "Unit Detail Modal and
 * Photo Lightbox" requirement). See `handleOpenDetail` below.
 */
import { useMemo, useRef, useState, type CSSProperties } from 'react';
import type { ClientOfferReviewShellProps } from './client-offer-review-shell-types';
import { createRestoreAllPeriodsToDefault } from './lib/restore-all-periods-to-default';
import { GRID_TEMPLATE_COLUMNS } from './lib/grid-template-columns';
import { buildReviewBanner } from './lib/build-review-banner';
import { ClientOfferReviewShellDialogs } from './client-offer-review-shell-dialogs';
import type { ClientDecision } from '@ooh/ui/components/client-offer-view';
import type {} from '@ooh/contracts';
import type { MapInstance } from '@ooh/ui/components/map-view';
import { useClientOfferReviewUnits } from './hooks/use-client-offer-review-units';
import { formatDisplayDate, formatDisplayDateRange } from '@ooh/ui/lib/date-format';
import {
  ClientOfferReviewFooterBar,
  ClientOfferReviewHeader,
  ClientOfferReviewMapPanel,
  ClientOfferReviewStatusBanner,
  ClientOfferReviewSummaryStats,
  ClientOfferReviewToolbar,
  ClientOfferReviewUnitList,
  REVIEW_MARKER_CATEGORY_COLORS,
  buildClientOfferReviewFilterFields,
  type ClientOfferReviewGranularity,
  type ClientOfferReviewModalKind,
} from '../client-offer-review';
import { useClientOfferReviewViewState } from './hooks/use-client-offer-review-view-state';
import { useClientOfferReviewShellActions } from './hooks/use-client-offer-review-shell-actions';
import {
  buildCustomPeriodItems,
  buildReviewMapPins,
  computeOfferReviewAggregates,
  mapClientDiffItemsToReview,
} from './lib/client-offer-review-shell-model';

// eslint-disable-next-line max-lines-per-function, complexity -- single composition root wiring ~10 presentational pieces, mirrors the mockup reference's shape
export function ClientOfferReviewShell(props: ClientOfferReviewShellProps) {
  const {
    snapshotUnits,
    enrichedUnitsById,
    showSupplierIdentity,
    clientName,
    offerTitle,
    versionNumber,
    publishedAt,
    campaignFrom,
    campaignTo,
    accessMode,
    loggedInEmail,
    bannerState,
    isPreview,
    decisions,
    onDecisionChange,
    selectedPeriods,
    onPeriodSelect,
    readOnly,
    roHint,
    onAccept,
    diff,
    prevVersion,
    labels,
    periodInquiry,
    onOpenUnitDetail,
    lockedRowIds,
    topBanner,
  } = props;

  // Uncontrolled fallback for the read-only version-preview route, which has no
  // useClientOfferSelections to own granularity — see ClientOfferReviewShellProps.granularity.
  const [localGranularity, setLocalGranularity] =
    useState<ClientOfferReviewGranularity>('HALF_MONTH');
  const granularity = props.granularity ?? localGranularity;

  const { units, periods, defaultPeriodIdsByUnitId } = useClientOfferReviewUnits(
    snapshotUnits,
    enrichedUnitsById,
    showSupplierIdentity,
    lockedRowIds,
    granularity,
    campaignFrom,
    campaignTo,
    props.frozenDimensions ?? false,
  );
  const [modal, setModal] = useState<ClientOfferReviewModalKind>(null);
  const [diffExpanded, setDiffExpanded] = useState(true);
  const mapInstanceRef = useRef<MapInstance | null>(null);
  const mapWrapRef = useRef<HTMLDivElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);
  const rowRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const view = useClientOfferReviewViewState({ units, decisions });
  const aggregates = computeOfferReviewAggregates(units, decisions);
  const customPeriodItems = periodInquiry
    ? buildCustomPeriodItems(aggregates.accU, selectedPeriods, defaultPeriodIdsByUnitId, periods)
    : [];
  const pins = useMemo(() => buildReviewMapPins(units, decisions), [units, decisions]);
  const filterFields = useMemo(
    () => buildClientOfferReviewFilterFields(units, labels.filterPanel),
    [units, labels.filterPanel],
  );

  const { banner, statusPill } = buildReviewBanner({
    bannerState,
    isPreview,
    accCount: aggregates.accU.length,
    accValue: aggregates.accValue,
    labels: labels.bannerModel,
  });

  function decide(unitId: string, target: Exclude<ClientDecision, null>) {
    onDecisionChange(unitId, decisions[unitId] === target ? null : target);
  }

  const restoreAllPeriodsToDefault = createRestoreAllPeriodsToDefault({
    acceptedUnits: aggregates.accU,
    selectedPeriods,
    defaultPeriodIdsByUnitId,
    onPeriodSelect,
  });

  /**
   * Per spec (client-offer-interactive-view), granularity switching re-maps every
   * accepted unit's selection to the new granularity's defaults.
   *
   * Controlled mode (interactive SSO/public routes): delegate entirely to
   * `props.onGranularityChange` (useClientOfferSelections.handleGranularityChange) —
   * it recomputes the axis for the NEW granularity synchronously before re-mapping,
   * avoiding this render's `defaultPeriodIdsByUnitId`, which still reflects the
   * OUTGOING granularity (React state updates are not synchronous).
   *
   * Uncontrolled mode (read-only version-preview route): there is no selection
   * state to keep in sync (decisions is always `{}`), so `restoreAllPeriodsToDefault`
   * is a no-op loop and the staleness above never manifests.
   */
  function handleGranularityChange(g: ClientOfferReviewGranularity) {
    if (props.onGranularityChange) {
      props.onGranularityChange(g);
      return;
    }
    setLocalGranularity(g);
    restoreAllPeriodsToDefault();
  }

  const { flyToUnit, focusUnit, toggleFullscreen, onLayoutStepChange } =
    useClientOfferReviewShellActions({ units, view, mapInstanceRef, mapWrapRef, rowRefs });

  /** A unit whose `mediaUnitId` cannot be resolved (no valid media unit link) is a silent no-op. */
  function handleOpenDetail(unitId: string) {
    const mediaUnitId = units.find((u) => u.id === unitId)?.mediaUnitId;
    if (mediaUnitId == null || !Number.isInteger(mediaUnitId) || mediaUnitId <= 0) return;
    onOpenUnitDetail?.(mediaUnitId);
  }

  const lightboxUnit = view.lightbox
    ? (units.find((u) => u.id === view.lightbox!.id) ?? null)
    : null;
  const footerVisible = bannerState !== 'accepted' && bannerState !== 'rejected';
  const [footerHeight, setFooterHeight] = useState<number | null>(null);
  const footerSize = footerHeight == null ? 'var(--co-map-footer-clearance)' : `${footerHeight}px`;
  const footerOffset = `calc(${footerSize} + var(--co-map-footer-gap))`;

  return (
    <div
      className="flex min-h-screen flex-col bg-background"
      style={
        footerVisible ? ({ '--co-map-footer-offset': footerOffset } as CSSProperties) : undefined
      }
      data-testid="client-offer-review-shell"
    >
      <ClientOfferReviewHeader
        accessMode={accessMode}
        offerTitle={offerTitle}
        offerMeta={`${clientName} · ${formatDisplayDate(publishedAt)}`}
        loggedInEmail={loggedInEmail}
        versionNumber={versionNumber}
        statusPill={statusPill}
        labels={labels.header}
      />
      {topBanner}
      <ClientOfferReviewStatusBanner
        banner={banner}
        showDiff={diff != null && diff.length > 0}
        prevVersion={prevVersion}
        diffExpanded={diffExpanded}
        onToggleDiff={() => setDiffExpanded((v) => !v)}
        diffItems={diff ? mapClientDiffItemsToReview(diff) : []}
        labels={labels.statusBanner}
      />
      <div className="flex flex-col gap-3.5 px-6 pt-3.5">
        <ClientOfferReviewSummaryStats
          unitCount={units.length}
          cityCount={new Set(units.map((u) => u.city)).size}
          campaignRange={
            campaignFrom && campaignTo ? formatDisplayDateRange(campaignFrom, campaignTo) : ''
          }
          totalNet={aggregates.totalNet}
          hasSold={aggregates.soldCount > 0}
          soldShort={String(aggregates.soldCount)}
          soldLabel={labels.unitList.unitCard.soldLabel}
          accCount={aggregates.accU.length}
          rejCount={aggregates.rejU.length}
          pendCount={aggregates.pendU.length}
          accValue={aggregates.accValue}
          pctAcc={aggregates.pctAcc}
          pctRej={aggregates.pctRej}
          pctPend={aggregates.pctPend}
          availCount={aggregates.avail.length}
          labels={labels.summaryStats}
        />
        <ClientOfferReviewToolbar
          query={view.query}
          onQuery={view.onQuery}
          filter={view.filter}
          onFilter={view.onFilter}
          counts={{
            ALL: units.length,
            ACCEPTED: aggregates.accU.length,
            REJECTED: aggregates.rejU.length,
            PENDING: aggregates.pendU.length,
          }}
          photoMode={view.photoMode}
          onPhotoMode={view.setPhotoMode}
          sortKey={view.sortKey}
          onSortKey={view.setSortKey}
          granularity={granularity}
          onGranularity={handleGranularityChange}
          pageSize={view.pageSize}
          onPageSize={view.onPageSizeChange}
          readOnly={readOnly}
          roHint={roHint}
          onSelectAll={() => setModal('selectAll')}
          onClearAll={() => setModal('clearAll')}
          shownLabel={`${view.visibleRows.length}/${view.rows.length}`}
          labels={labels.toolbar}
          filterFields={filterFields}
          panelFilters={view.panelFilters}
          onPanelFiltersChange={view.onPanelFiltersChange}
          filterPanelLabels={labels.filterPanel}
        />
      </div>
      {/* Keep footer clearance on the list itself. Bottom padding on this grid
          shortens the sticky containing block and releases the map early. */}
      <div
        data-co-main
        className="grid flex-1 items-start gap-4 px-6 pt-3.5"
        style={{ gridTemplateColumns: GRID_TEMPLATE_COLUMNS[view.layoutStep] }}
      >
        <ClientOfferReviewUnitList
          listRef={listRef}
          rowRefs={rowRefs}
          visibleRows={view.visibleRows}
          startIndex={view.startIndex}
          totalUnitCount={units.length}
          decisions={decisions}
          selectedPeriods={selectedPeriods}
          activeId={view.activeId}
          pulseId={view.pulseId}
          photoMode={view.photoMode}
          periods={periods}
          readOnly={readOnly}
          roHint={roHint}
          onLocate={(id) => flyToUnit(id)}
          onOpenDetail={handleOpenDetail}
          onOpenLightbox={(id) => view.setLightbox({ id, idx: 0 })}
          onAccept={(unit) => decide(unit.id, 'accepted')}
          onReject={(unit) => decide(unit.id, 'rejected')}
          onTogglePeriod={onPeriodSelect}
          page={view.page}
          pageCount={view.pageCount}
          onPageChange={view.onPageChange}
          noResults={view.noResults}
          onClearFilters={view.clearFilters}
          labels={labels.unitList}
        />
        <ClientOfferReviewMapPanel
          pins={pins}
          categoryColors={REVIEW_MARKER_CATEGORY_COLORS}
          selectedPinIds={view.activeId ? new Set([view.activeId]) : new Set()}
          onPinClick={focusUnit}
          mapInstanceRef={mapInstanceRef}
          mapWrapRef={mapWrapRef}
          onToggleFullscreen={toggleFullscreen}
          layoutStep={view.layoutStep}
          onLayoutStepChange={onLayoutStepChange}
          labels={labels.mapPanel}
        />
      </div>

      <ClientOfferReviewFooterBar
        // Mirrors v1's OfferAcceptFooter: hidden only once a real terminal decision
        // exists, not merely because previewMode disables the buttons — a preview
        // must keep the footer visible-and-disabled (tasks.md 3.6).
        visible={footerVisible}
        onHeightChange={setFooterHeight}
        accCount={aggregates.accU.length}
        rejCount={aggregates.rejU.length}
        pendCount={aggregates.pendU.length}
        accValue={aggregates.accValue}
        versionNumber={versionNumber}
        readOnly={readOnly}
        roHint={roHint}
        onAccept={() => setModal('accept')}
        customPeriodItems={customPeriodItems}
        onRestoreDefaultPeriods={restoreAllPeriodsToDefault}
        onSelectUnit={focusUnit}
        labels={labels.footerBar}
        periodInquiry={
          periodInquiry
            ? {
                submitLabel: labels.inquirySubmit,
                onSubmit: periodInquiry.onSubmit,
                isPending: periodInquiry.isPending,
              }
            : undefined
        }
      />

      <ClientOfferReviewShellDialogs
        modal={modal}
        onModalChange={setModal}
        units={units}
        aggregates={aggregates}
        versionNumber={versionNumber}
        onDecisionChange={onDecisionChange}
        onAccept={onAccept}
        lightbox={view.lightbox}
        lightboxUnit={lightboxUnit}
        onLightboxChange={view.setLightbox}
        labels={labels}
      />
    </div>
  );
}
